import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import React, { FC } from 'react'
import { FormattedMessage } from 'react-intl'
import ProductPriceAvailabilityRow from './ProductPriceAvailabilityRow'
import { InitialShipEstimate } from '@src/components/ProductPriceAvailability/ProductPriceAvailabilityFormBody'

const useStyles = makeStyles()((theme) => ({
  table: {
    margin: theme.spacing(2, 0, 6),
  },
  tableHead: {
    whiteSpace: 'nowrap',
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium,
    padding: theme.spacing(0, 3),
    // Letter spacing and line height to match Body1 properties without having to wrap each header.
    letterSpacing: '-0.12px',
    lineHeight: '16px',
    [theme.breakpoints.between('sm', 'lg')]: {
      whiteSpace: 'normal',
      padding: theme.spacing(0, 1),
    },
  },
  tableRow: {
    cursor: 'pointer',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.light,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  loadingSkeleton: {
    margin: theme.spacing(2, 0, 5),
  },
}))

export interface ProductPriceAvailabilityTableProps {
  loading?: boolean
  materialPricing: ValidMaterialPricingDetailFragment[] | undefined
  onRowClick: (material: ValidMaterialPricingDetailFragment) => void
  rowSelected: (material: ValidMaterialPricingDetailFragment) => boolean
}

const ProductPriceAvailabilityTable: FC<ProductPriceAvailabilityTableProps> = ({
  loading,
  materialPricing,
  onRowClick,
  rowSelected,
}) => {
  const { classes } = useStyles()

  // gets the primary availability otherwise returns the first availability
  const getAvailability = (material: ValidMaterialPricingDetailFragment) => {
    const [firstAvailability] = material?.availabilities ?? []
    return (
      material.availabilities?.find(
        ({ messageType }) => messageType === 'primary'
      ) ?? firstAvailability
    )
  }

  return loading ? (
    <Box className={classes.loadingSkeleton}>
      {Array(3)
        .fill({})
        .map((_, index) => (
          <Skeleton animation="wave" height={30} key={index} />
        ))}
    </Box>
  ) : (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell className={classes.tableHead}>
            <FormattedMessage id="PACK_SIZE" defaultMessage="Pack Size" />
          </TableCell>
          <TableCell className={classes.tableHead}>
            <FormattedMessage id="SKU" defaultMessage="SKU" />
          </TableCell>
          <TableCell className={classes.tableHead}>
            <FormattedMessage id="AVAILABILITY" defaultMessage="Availability" />
          </TableCell>
          <TableCell className={classes.tableHead}>
            <FormattedMessage id="PRICE" defaultMessage="Price" />
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {materialPricing?.map((material, index) => (
          <TableRow
            key={material.materialNumber}
            onClick={() => onRowClick(material)}
            selected={rowSelected(material)}
            className={classes.tableRow}
          >
            <ProductPriceAvailabilityRow
              key={index}
              initialShipEstimate={
                <InitialShipEstimate
                  availability={getAvailability(material)!}
                  leadTime={material?.leadTime}
                />
              }
              material={material}
            />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default ProductPriceAvailabilityTable
